import { Injectable } from '@angular/core';
import { SessionStorageService } from './session-storage.service';
import { SessionContextService } from './session-context.service';
import { MultipleSpinnerService } from './multiple-spinner.service';
import { CheckSheetService } from '@core/services/apis/check-sheet.service';
import { OfficeService } from '@core/services/apis/office.service';
import { LOCAL_STORAGE_KEY, SESSION_STORAGE_KEY } from '@core/configs';
import { CommonHelper } from './helper.service';
import { BathingScreenSortType, KbnStatus } from '@core/constants/enum.common';
import { Utils } from '@core/helper/utils.helper';
import { katakanaToHepburn } from '@core/helper/japanese-converter.helper';
import { BATH_SCREEN } from '@core/constants/text.common';
import { forkJoin, lastValueFrom } from 'rxjs';
import moment from 'moment';
import { LeaveType } from '@core/models/enums/common.enum';
import { cloneDeep } from 'lodash';
import { BathDailyModel, BatheType, BathMaster, BathRoomSummary, BathDaily, BathRoomRule, DAIS, BathDais } from '@core/models/models/bathe.model';
import { BathRoomSettingService } from '../apis/bath-room-setting.service';
import { PriorityRecheck } from '@core/models/models/priority-recheck.model';
import { BatheMethodList, parseDaiTableToBathDais, isDateMatchingBathSchedule, isDaiScheduledOnDate, isDaiScheduledAtDate, isDaiValidForDisplayOnDate, formatDateToLocalString, BatheHelper, reassignSortNoWithInvalidAtEnd, getTodayJPStr, mapJsonDais, getForceValidDaiCds, mergeForceValidRecords } from '@modules/bathe-management/bathe.helper';
import { resolveDaiConfig } from '@modules/bathe-management/dai-snapshot.helper';
import { DynamoDBKeyHelper } from '@core/helper';

export interface FetchScreenRecordOptions {
    officeType: number;
    tokenizer: any;
    bathingScreenConfig?: {
        sortType?: BathingScreenSortType;
        unitSelectedKey?: string | string[];
        groupSelectedKey?: string | string[];
        bathRoomSelectedKey?: string;
        isFlashingMoshiokuri?: boolean;
    };
    necessaryProperties?: string[];
    isManualSync: boolean;
}

export interface FetchScreenRecordResult {
    originNotes: any[];
    processedNotes: any[];
    bathingNoteRecords: any[];
    daiCdsCarryOut: string[];  // List of daiCd with STATUS_CARRY_OUT
    daiCdsCancel: string[];     // List of daiCd with STATUS_CANCEL
    pendingBathDailyRecords?: BathDaily[];  // List of BathDaily records that need to be saved (Case B new records + Case A ISVALID changes)
}

export interface GenerateBathOptions {
    userDateMatrix?: string[][];
    bathScheduledMatrix?: string[][];
    isNyuSho?: boolean;
    isDaiValidOnDate?: (daiCd: string, dateStr: string) => boolean;
}

@Injectable({
    providedIn: 'root'
})
export class BathingScreenService {
    private readonly successfulHttpStatusCodes: number[] = [200, 204];
    bathRoomInfos: BathMaster[] = [];

    // Async save status tracking (persists across component lifecycle)
    isAsyncSavingBathDaily: boolean = false;
    asyncSaveCompletedCount: number = 0;
    asyncSaveTotalCount: number = 0;

    constructor(
        private sessionStorageService: SessionStorageService,
        private sessionContextService: SessionContextService,
        private loadingScreenCoordinator: MultipleSpinnerService,
        private checkSheetService: CheckSheetService,
        private officeService: OfficeService,
        private bathRoomSettingService: BathRoomSettingService,
    ) { }

    /**
     * Fetch screen records from API and process them
     * @param options Configuration options for fetching and processing records
     * @returns Processed screen records
     */
    async fetchingScreenRecord(options: FetchScreenRecordOptions, isBatheManagement: boolean = false): Promise<FetchScreenRecordResult> {
        try {
            this.loadingScreenCoordinator.show();

            const response: any = await lastValueFrom(this.checkSheetService.getByOffice());
            if (!this.successfulHttpStatusCodes.includes(response?.status ?? 500)
                || !Array.isArray(response?.result)) {
                return this.getEmptyResult();
            }

            let daiNoteRecords = response.result.flatMap(this.parseData.bind(this));
            let processRecords = await this.enrichUserData(daiNoteRecords, options.officeType, options.bathingScreenConfig?.isFlashingMoshiokuri ?? true, isBatheManagement);

            const originNotes = structuredClone(processRecords);
            const processedNotes = structuredClone(processRecords);

            // Group by DAICD and sort follow config
            let tempNotes = this.groupAndSortData(originNotes, options);
            // Bathe special filtering
            const batheFilterResult = this.batheFiltering(tempNotes);
            tempNotes = batheFilterResult.temptNodes;
            // Filter the necessary properties
            if (options.necessaryProperties && options.necessaryProperties.length > 0) {
                tempNotes = this.necessaryPropertyFiltering(tempNotes, options.necessaryProperties);
            }

            return {
                originNotes,
                processedNotes,
                bathingNoteRecords: tempNotes,
                daiCdsCarryOut: batheFilterResult.daiCdsCarryOut,
                daiCdsCancel: batheFilterResult.daiCdsCancel
            };
        }
        catch (error) {
            console.error('Error fetching screen records:', error);
            return this.getEmptyResult();
        }
        finally {
            this.loadingScreenCoordinator.hide();
        }
    }

    async fetchBathScheduleRecord(
        options: FetchScreenRecordOptions,
        dailyRecordLake: BathDaily[],
        bathMasters: BathMaster[],
        generateOptions?: GenerateBathOptions,
        daiBelongUsers?: any[]
    ): Promise<FetchScreenRecordResult> {
        try {
            this.loadingScreenCoordinator.show();

            const response: any = await lastValueFrom(this.checkSheetService.getByOffice());
            if (!this.successfulHttpStatusCodes.includes(response?.status ?? 500)
                || !Array.isArray(response?.result)) {
                return this.getEmptyResult();
            }
            let daiNoteRecords = response.result.flatMap(this.parseData.bind(this));
            
            let processRecords = await this.enrichUserData(daiNoteRecords, options.officeType, options.bathingScreenConfig?.isFlashingMoshiokuri ?? true);

            const mergeResult = this.mergeBathingData(dailyRecordLake, bathMasters, processRecords, generateOptions, daiBelongUsers, options.isManualSync);
            processRecords = mergeResult.mergedRecords;
            const pendingBathDailyRecords = mergeResult.pendingBathDailyRecords;

            let originNotes = structuredClone(processRecords);
            originNotes = originNotes.map(record => ({
                ...record,
                bathRoomName: this.getBathRoomNameByCd(record.bathRoomCd)
            }));

            const processedNotes = structuredClone(processRecords);

            const bathDailyRoomSet = new Set(
                (dailyRecordLake ?? []).map(x => {
                    const parts = x.PartitionKey?.split('#') ?? [];
                    const bathPart = parts.find(p => p.startsWith('bathroom_'));
                    return bathPart ? bathPart.replace('bathroom_', '') : '';
                }).filter(x => x)
            );
            // Group by DAICD and sort follow config
            let tempNotes = this.groupAndSortData(originNotes, options, bathDailyRoomSet);
            // Bathe special filtering
            const batheFilterResult = this.batheFiltering(tempNotes);
            tempNotes = batheFilterResult.temptNodes;
            // Filter the necessary properties
            if (options.necessaryProperties && options.necessaryProperties.length > 0) {
                tempNotes = this.necessaryPropertyFiltering(tempNotes, options.necessaryProperties);
            }
            return {
                originNotes,
                processedNotes,
                bathingNoteRecords: tempNotes,
                daiCdsCarryOut:[],
                daiCdsCancel:[],
                pendingBathDailyRecords: pendingBathDailyRecords
            };
        }
        catch (error) {
            console.error('Error fetching screen records:', error);
            return this.getEmptyResult();
        }
        finally {
            this.loadingScreenCoordinator.hide();
        }
    }

    private getBathRoomNameByCd(bathRoomCd: string): string {
        if (!bathRoomCd || !this.bathRoomInfos?.length) return "";

        const found = this.bathRoomInfos.find((x: any) => {
            const sortKey = x.SortKey.split("_");
            const roomCd = sortKey[sortKey.length - 1];
            return roomCd === bathRoomCd;
        });

        return found?.BATHNAME ?? "";
    }

    /**
     * Merge DailyNote, BathDaily (from dailyRecordLake), and BathMaster (BATHDAIS)
     * Similar to BatheManagementComponent.processCurrentBatheData()
     *
     * @param isManualSync - If true, applies sync logic similar to onSyncClick:
     *   - For existing bath daily: filter invalid users, add new scheduled users, use existing PK/SK
     *   - For non-existing bath daily: generate from bath masters with NEW_ prefix
     */
    private mergeBathingData(
        dailyRecordLake: BathDaily[],
        bathMasters: BathMaster[],
        bathingNoteRecords: any[],
        generateOptions?: GenerateBathOptions,
        daiBelongUsers?: any[],
        isManualSync: boolean = false
    ): { mergedRecords: any[], pendingBathDailyRecords: BathDaily[] } {
        const mergedRecords: any[] = [];
        const pendingBathDailyRecords: BathDaily[] = []; // Track bathrooms that need daily logs created
        
        // Get current date key
        const currentDate = getTodayJPStr();
        const year = new Date(currentDate).getFullYear();
        const month = String(new Date(currentDate).getMonth() + 1).padStart(2, '0');
        const day = String(new Date(currentDate).getDate()).padStart(2, '0');
        const dateKey = `date_${year}${month}${day}`; // e.g., "date_20260128"

        // Get all daily logs for current date
        const dailyLogsForDate = dailyRecordLake.filter(item => item.SortKey === dateKey);

        // Collect all daiCds in all rooms (for Filter 2 - prevent duplicate in multiple rooms)
        const allDaisInAllRooms = new Map<string, string>(); // daiCd -> roomId
        for (const dailyLog of dailyLogsForDate) {
            const roomId = DynamoDBKeyHelper.getValueByClass(dailyLog.PartitionKey, "bathroom") ?? "";
            dailyLog.DAIS.forEach((dai: any) => {
                if (dai.DAICD) {
                    allDaisInAllRooms.set(dai.DAICD, roomId);
                }
            });
        }

        // Process each bathroom
        for (const bathMaster of bathMasters) {
            const bathRoomCd = DynamoDBKeyHelper.getValueByClass(bathMaster.SortKey, "bathroom") ?? "";
            const bathRoomKey = `bathroom_${bathRoomCd}`;
            const daiListForBathroom = bathMaster.BATHDAIS || [];

            // Find bath daily record for this room and date
            const bathDailyForRoom = dailyLogsForDate.find(
                item => item.PartitionKey?.includes(bathRoomKey)
            );

            if (bathDailyForRoom) {
                // Case A: Bath daily EXISTS for this room
                // Map stores daiItem along with BLOCKCD/UNITCD from the parent BathMaster
                let allDaiMasters = new Map<string, { daiItem: BathDais, BLOCKCD?: string, UNITCD?: string, BATHNAME?: string }>();
                (bathDailyForRoom?.DAIS || []).forEach((dai: any) => {
                    if (dai.DAICD) {
                        // Find bathdai item in list bathmaster
                        for (const bm of bathMasters) {
                            const bathDaiItem = bm.BATHDAIS.find(bathDai => bathDai.DAICD === dai.DAICD);
                            if (bathDaiItem) {
                                // Store daiItem with BLOCKCD/UNITCD from the BathMaster (bathroom)
                                allDaiMasters.set(dai.DAICD, {
                                    daiItem: bathDaiItem,
                                    BLOCKCD: bm.BLOCKCD,
                                    UNITCD: bm.UNITCD,
                                    BATHNAME: bm.BATHNAME
                                });
                                break;
                            }
                        }
                    }
                });

                // A day a dai move created (FORCEVALIDDAIS): render it like a day with no BathDaily -
                // generated from this room's master, failing dais dropped - plus the moved dais from
                // the day's own entries, always valid, at the end. Write it back once even when
                // nothing differs: that upsert clears the flag server-side, and drops the failing
                // dais from DAIS so a later normal render cannot bring them back.
                const forceSet = generateOptions?.isDaiValidOnDate
                    ? getForceValidDaiCds(bathDailyForRoom, false)
                    : null;
                if (forceSet && generateOptions) {
                    const generated = this.processBathDailyNotExists(
                        bathRoomCd,
                        daiListForBathroom,
                        bathingNoteRecords,
                        allDaisInAllRooms,
                        generateOptions,
                        this.getBathRoomRuleFromBathRule(bathMaster.BATHRULE),
                        bathMaster.BLOCKCD,
                        bathMaster.UNITCD,
                        daiBelongUsers
                    ).generatedRecords;
                    // Reuse the exists-path mapping on the forced entries only; their stored
                    // ISVALID is irrelevant (forced valid), so no generateOptions recompute.
                    const forcedDay = Object.assign(new BathDaily(), bathDailyForRoom, {
                        DAIS: bathDailyForRoom.DAIS.filter(dai => forceSet.has(dai.DAICD ?? ''))
                    });
                    const forced = this.processBathDailyExists(
                        forcedDay,
                        allDaiMasters,
                        bathingNoteRecords,
                        daiBelongUsers,
                        undefined,
                        getTodayJPStr()
                    ).records;
                    const forceRecords = mergeForceValidRecords(generated, forced, forceSet);
                    mergedRecords.push(...forceRecords);

                    const bathDailyToSave = new BathDaily();
                    bathDailyToSave.PartitionKey = bathDailyForRoom.PartitionKey; // EXISTING PK
                    bathDailyToSave.SortKey = bathDailyForRoom.SortKey;           // EXISTING SK
                    bathDailyToSave.INSDATE = bathDailyForRoom.INSDATE;
                    bathDailyToSave.INSSTFCD = bathDailyForRoom.INSSTFCD;
                    bathDailyToSave.ITEMVER = bathDailyForRoom.ITEMVER ?? null;
                    bathDailyToSave.LogTable = '';
                    bathDailyToSave.DAIS = forceRecords.map(item => {
                        const dais = new DAIS();
                        dais.DAICD = item.daiCd || item?.user?.daicd || '';
                        // Generated rows carry the method as text only (processBathDailyNotExists).
                        dais.BATHMETHODCD = item.bathMethodCd
                            ?? BatheMethodList.find(m => m.text === item.bathMethod)?.id
                            ?? 0;
                        dais.SORTNO = item.sortNo;
                        dais.ISVALID = true;
                        return dais;
                    }).filter(d => !!d.DAICD);

                    pendingBathDailyRecords.push(bathDailyToSave);
                    continue;
                }

                // Unified Mode: ISVALID evaluation for both auto and manual sync
                // (Old manual sync path using processBathDailyExistsWithFilter is retired)
                const today = getTodayJPStr();
                const { records, hasChanges } = this.processBathDailyExists(
                    bathDailyForRoom,
                    allDaiMasters,
                    bathingNoteRecords,
                    daiBelongUsers,
                    generateOptions,
                    today
                );

                // Reassign sortNo: valid (1,2,3...), invalid (-999, moved to end)
                const reassigned = this.reassignSortNoForDisplay(records);

                // Display: only show valid items (sortNo !== -999)
                mergedRecords.push(...reassigned.filter(item => item.sortNo !== -999));

                // If any item changed ISVALID state → queue save for this room
                if (hasChanges) {
                    const bathDailyToSave = new BathDaily();
                    bathDailyToSave.PartitionKey = bathDailyForRoom.PartitionKey; // EXISTING PK
                    bathDailyToSave.SortKey = bathDailyForRoom.SortKey;           // EXISTING SK
                    // Upsert replaces the whole DynamoDB item, so INSDATE/INSSTFCD must ride along
                    bathDailyToSave.INSDATE = bathDailyForRoom.INSDATE;
                    bathDailyToSave.INSSTFCD = bathDailyForRoom.INSSTFCD;
                    // The version this day was loaded at; the server refuses the write-back if the
                    // day moved on since (an entry appended by a dai move, say) rather than dropping it.
                    bathDailyToSave.ITEMVER = bathDailyForRoom.ITEMVER ?? null;
                    bathDailyToSave.LogTable = '';
                    bathDailyToSave.DAIS = reassigned.map(item => {
                        const dais = new DAIS();
                        dais.DAICD = item.daiCd || item?.user?.daicd || '';
                        dais.BATHMETHODCD = item.bathMethodCd ?? 0;
                        dais.SORTNO = item.sortNo ?? -999;
                        dais.ISVALID = item.isValidForDisplay ?? true;
                        return dais;
                    }).filter(d => !!d.DAICD);

                    pendingBathDailyRecords.push(bathDailyToSave);
                }
            } else if (generateOptions?.isDaiValidOnDate) {
                // Case B: Bath daily does NOT exist - generate from bath masters
                // Convert BATHRULE to BathRoomRule (same mapping as BatheManagementComponent)
                const bathRoomRule = this.getBathRoomRuleFromBathRule(bathMaster.BATHRULE);
                const result = this.processBathDailyNotExists(
                    bathRoomCd,
                    daiListForBathroom,
                    bathingNoteRecords,
                    allDaisInAllRooms,
                    generateOptions,
                    bathRoomRule,
                    bathMaster.BLOCKCD,
                    bathMaster.UNITCD,
                    daiBelongUsers
                );
                mergedRecords.push(...result.generatedRecords);

                // If records were generated, create a BathDaily record for async saving
                // if (result.generatedRecords.length > 0) {
                //     pendingBathDailyRecords.push(result.bathDailyRecord);
                // }
            }
        }

        return { mergedRecords, pendingBathDailyRecords };
    }

    /**
     * Process case when bath daily EXISTS for the room
     * Logic: Loop through each dai in bathDailyForRoom.DAIS,
     * then lookup bath master information for each daiCd
     * Similar to BatheManagementComponent.processBathDailyExists()
     */
    private processBathDailyExists(
        bathDaily: BathDaily,
        allDaiMasters: Map<string, { daiItem: BathDais, BLOCKCD?: string, UNITCD?: string, BATHNAME?: string }>,
        bathingNoteRecords: any[],
        daiBelongUsers?: any[],
        generateOptions?: GenerateBathOptions,
        dateStr?: string
    ): { records: any[], hasChanges: boolean } {
        const result: any[] = [];
        let hasChanges = false;
        const today = dateStr ?? getTodayJPStr();

        // Extract bathRoomCd from daily record's PartitionKey
        const bathRoomCd = DynamoDBKeyHelper.getValueByClass(bathDaily.PartitionKey, "bathroom") ?? "";

        // Sort DAIS: items with SORTNO=-999 (previously invalid) go to end
        // so if they become valid again they get assigned the highest sortNo (bottom of room)
        const sortedDais = [...bathDaily.DAIS].sort((a: any, b: any) => {
            if (a.SORTNO === -999 && b.SORTNO !== -999) return 1;
            if (a.SORTNO !== -999 && b.SORTNO === -999) return -1;
            return (a.SORTNO ?? 0) - (b.SORTNO ?? 0);
        });

        // Base: Loop through each dai in bath daily record (bathDailyForRoom.DAIS)
        sortedDais.forEach((dai: any) => {
            const daiCd = dai.DAICD ?? '';
            if (!daiCd) return;

            // Find corresponding note and master dai record
            let noteItem = bathingNoteRecords.find(n => n?.user?.daicd === daiCd);
            // Lookup bath master information for this daiCd
            const daiMasterEntry = allDaiMasters.get(daiCd);

            // Look up user info from daiBelongUsers (used for stub and gender)
            const daiUser = daiBelongUsers?.find(u => u.daicd === daiCd);
            const daiUserGender = daiUser?.sex === 1 ? '男' : '女';

            // Prefer this entry's own frozen snapshot once one exists; the live master
            // record (when the dai is still registered somewhere) is only the fallback
            // source - see dai-snapshot.helper.ts. A dai with neither a snapshot nor a
            // master entry resolves to 'orphan' below.
            const resolved = resolveDaiConfig(
                dai,
                bathDaily.SCHEMAVER,
                daiMasterEntry?.daiItem,
                daiMasterEntry?.BLOCKCD,
                daiMasterEntry?.UNITCD,
                daiMasterEntry?.BATHNAME
            );

            // Orphan: the dai still lives in BathDaily but has neither a frozen snapshot
            // nor a live BathMaster entry (removed from every bathroom's BATHDAIS, and
            // never snapshotted). BathMaster-side fields are left empty and the ISVALID
            // recompute is skipped - there is no config left to evaluate against. The
            // BathDaily record stays authoritative: its stored ISVALID keeps deciding
            // valid/invalid, and from here the record flows through the normal
            // sortNo/display/save path like any other.
            if (resolved.source === 'orphan') {
                // Always guarantee a user object: an orphan flagged valid in BathDaily still
                // reaches the downstream filters, which would dereference an undefined user.
                if (!noteItem) {
                    noteItem = this.buildNoteStub(daiCd, daiUser);
                }
                if (noteItem?.user) {
                    noteItem.user.blockcd = daiUser?.blockcd ?? noteItem.user.blockcd ?? '';
                    noteItem.user.unitcd = daiUser?.unitcd ?? noteItem.user.unitcd ?? '';
                }

                result.push({
                    bathRoomCd: bathRoomCd,
                    // BathMaster-side values empty (link lost)
                    blockCd: '',
                    unitCd: '',
                    bathWeeks: null,
                    bathDays: null,
                    bathMethod: BatheMethodList.find(m => m.id === dai.BATHMETHODCD)?.text || '',
                    priority: {
                        priority: '',
                        recheck: this.resolveRecheckTag(noteItem?.vital)
                    } as PriorityRecheck,
                    treatmentNoteMsg: { message: "" },
                    gender: daiUserGender,
                    ...(noteItem || {}),
                    // BathDaily-side values preserved as-is. Assigned after the spread so a
                    // note payload can never clobber what we are trying to preserve.
                    daiCd: daiCd,
                    bathMethodCd: dai.BATHMETHODCD ?? 0,
                    isValidForDisplay: dai.ISVALID ?? true,
                    sortNo: dai.SORTNO ?? null
                });
                return;
            }

            // If no note record found (e.g. yotei re-added but not yet synced),
            // create a minimal stub so downstream code has a valid user object
            if (!noteItem && daiUser) {
                noteItem = this.buildNoteStub(daiCd, daiUser, resolved.blockCd ?? undefined, resolved.unitCd ?? undefined);
            }

            // Update noteItem.user with blockcd and unitcd from BathMaster (if noteItem exists)
            if (noteItem?.user) {
                noteItem.user.blockcd = daiUser?.blockcd ?? resolved.blockCd ?? '';
                noteItem.user.unitcd = daiUser?.unitcd ?? resolved.unitCd ?? '';
            }

            const recheckValue = this.resolveRecheckTag(noteItem?.vital);
            const priorityRecheck: PriorityRecheck = {
                priority: BatheHelper.getPriorityFromBatheType(resolved.priorityCd as BatheType),
                recheck: recheckValue
            };

            // === ISVALID evaluation (only when generateOptions is provided) ===
            // Shared valid/invalid rule (see isDaiValidForDisplayOnDate) so this screen and
            // bathe-management resolve identity identically.
            if (generateOptions?.isDaiValidOnDate) {
                const oldIsValid = dai.ISVALID;
                const newIsValid = isDaiValidForDisplayOnDate({
                    daiCd,
                    checkDate: new Date(today),
                    dateStr: today,
                    isNyuSho: generateOptions.isNyuSho ?? false,
                    bathWeeks: resolved.bathWeeks,
                    bathDays: resolved.bathDays,
                    bathScheduledMatrix: generateOptions.bathScheduledMatrix,
                    isDaiValidOnDate: generateOptions.isDaiValidOnDate
                });

                // Detect change
                if (oldIsValid !== newIsValid) {
                    hasChanges = true;
                }
                dai.ISVALID = newIsValid; // Mutate in-place (update BathDaily cache)
            }
            // === END ISVALID evaluation ===

            const mergedItem = {
                daiCd: daiCd,
                bathMethodCd: resolved.bathMethodCd ?? 0,
                isValidForDisplay: dai.ISVALID ?? true,
                sortNo: dai.SORTNO ?? null,
                bathRoomCd: bathRoomCd,
                blockCd: resolved.blockCd,
                unitCd: resolved.unitCd,
                bathWeeks: resolved.bathWeeks,
                bathDays: resolved.bathDays,
                bathMethod: BatheMethodList.find(m => m.id === resolved.bathMethodCd)?.text || '',
                priority: priorityRecheck,
                treatmentNoteMsg: { message: resolved.treatmentNotes || "" },
                gender: daiUserGender,
                ...(noteItem || {})
            };

            result.push(mergedItem);
        });

        return { records: result, hasChanges };
    }

    /**
     * Build a minimal note stub so downstream code always has a valid user object.
     * blockCd/unitCd act as a fallback when the user is missing them (BathMaster values).
     */
    private buildNoteStub(daiCd: string, daiUser: any, blockCd?: string, unitCd?: string): any {
        return {
            user: {
                daicd: daiCd,
                isleave: daiUser?.isleave ?? false,
                blockcd: daiUser?.blockcd ?? blockCd ?? '',
                unitcd: daiUser?.unitcd ?? unitCd ?? '',
                isWarning: false,
                isHighlightRecord: true,
                face_img: daiUser?.face_img ?? '',
                family: daiUser?.family ?? '',
                given: daiUser?.given ?? '',
                kanafamily: daiUser?.kanafamily ?? '',
                kanagiven: daiUser?.kanagiven ?? '',
                sex: daiUser?.sex ?? 0,
                roomcd: daiUser?.roomcd ?? '',
                roomnm: daiUser?.roomnm ?? '',
                unitobjcd: daiUser?.unitobjcd ?? '',
                bedcd: daiUser?.bedcd ?? '',
                romanjiFamily: daiUser?.kanafamily ? katakanaToHepburn(daiUser.kanafamily) : '',
                romanjiGiven: daiUser?.kanagiven ? katakanaToHepburn(daiUser.kanagiven) : '',
            },
            vital: []
        };
    }

    /**
     * Decide whether the 再検 (recheck) tag should show for a set of vital records.
     *
     * Invariant: a recheck is considered handled once the most recent vital group
     * (same Sdate to the minute) also contains a valid measurement. The 再検 request
     * (`content` includes "再検") and the follow-up measurement land in the same
     * time group; a valid measurement is one whose recheck `content` is null/empty/
     * whitespace. So the newest group wins - if it holds a recheck AND a valid
     * measurement alongside it, the tag is cleared; otherwise the tag stays.
     *
     * This replaces the fragile `vital[0]` lookup (vital[] is not sorted by the FE),
     * which both missed rechecks not at index 0 and never auto-resolved after a
     * valid re-measurement. Shared by the tag (StatusComponent) and the 待ち filter.
     */
    private resolveRecheckTag(vitals: any[]): string {
        if (!vitals?.length) {
            return "";
        }

        // Group by Sdate to the minute (substring 0..16 tolerates "HH:mm" and
        // "HH:mm:ss"; a null/missing date falls into the "" key which sorts lowest).
        const groups = new Map<string, any[]>();
        for (const vital of vitals) {
            const key = (vital?.date ?? "").toString().substring(0, 16);
            const group = groups.get(key);
            if (group) {
                group.push(vital);
            } else {
                groups.set(key, [vital]);
            }
        }

        const latestKey = Array.from(groups.keys()).sort().pop() ?? "";
        const latest = groups.get(latestKey) ?? [];

        const hasRecheck = latest.some(v => v?.recheck?.content?.includes("再検"));
        if (!hasRecheck) {
            return "";
        }

        // Valid re-measurement = recheck content null/empty/whitespace.
        const hasValid = latest.some(v => !v?.recheck?.content?.trim());
        return (latest.length > 1 && hasValid) ? "" : "再検";
    }

    // /**
    //  * [RETIRED] processBathDailyExistsWithFilter
    //  * Previously used for manual sync mode (filter hard + add new users).
    //  * Retired: manual sync now uses the same ISVALID evaluation path as auto sync
    //  * (processBathDailyExists with generateOptions).
    //  */
    // private processBathDailyExistsWithFilter(...): any[] { ... }

    /**
     * Reassign sortNo: valid items get 1,2,3..., invalid items get -999 and move to end
     * Mirror of bathe.helper.reassignSortNoWithInvalidAtEnd() but for plain any[] objects
     */
    private reassignSortNoForDisplay(items: any[]): any[] {
        const validItems = items.filter(item => item.isValidForDisplay !== false);
        const invalidItems = items.filter(item => item.isValidForDisplay === false);

        const validWithSortNo = validItems.map((item, index) => ({ ...item, sortNo: index + 1 }));
        const invalidWithSortNo = invalidItems.map(item => ({ ...item, sortNo: -999 }));

        return [...validWithSortNo, ...invalidWithSortNo];
    }

    /**
     * Process case when bath daily does NOT exist - generate from bath masters
     * Similar to BatheManagementComponent.processBathDailyNotExists()
     */
    private processBathDailyNotExists(
        roomId: string,
        daiListForBathroom: BathDais[],
        bathingNoteRecords: any[],
        allDaisInAllRooms: Map<string, string>,
        generateOptions: GenerateBathOptions,
        bathRoomRule: BathRoomRule = BathRoomRule.None,
        blockCd?: string,
        unitCd?: string,
        daiBelongUsers?: any[]
    ): { generatedRecords: any[], bathDailyRecord: BathDaily } {
        const today = getTodayJPStr();
        const checkDate = new Date(today);
        const result: any[] = [];

        for (const daiItem of daiListForBathroom) {
            const daiCd = daiItem.DAICD;
            if (!daiCd) continue;

            // Filter 1: isDaiValidOnDate - check if user has valid yotei and daibelong
            if (!generateOptions.isDaiValidOnDate?.(daiCd, today)) continue;

            // Filter 2: Not in another room (prevent showing user in multiple rooms)
            if (allDaisInAllRooms.has(daiCd) && allDaisInAllRooms.get(daiCd) !== roomId) continue;

            // Filter 3: Date matches bath schedule
            if (generateOptions.isNyuSho) {
                // For Nyusho: use isDateMatchingBathSchedule
                if (!isDateMatchingBathSchedule(checkDate, (daiItem?.BATHWEEKS ?? ""), (daiItem?.BATHDAYS ?? ""), true)) continue;
            } else {
                // For Tsusho: use isDaiScheduledOnDate with bath scheduled matrix
                if (!isDaiScheduledOnDate(
                    generateOptions.bathScheduledMatrix || [],
                    daiCd, checkDate, (daiItem?.BATHWEEKS ?? ""), (daiItem?.BATHDAYS ?? ""), false
                )) continue;
            }

            // Find matching note record (noteItem can be undefined)
            let noteItem = bathingNoteRecords.find(n => n?.user?.daicd === daiCd);

            // Get gender from daiBelongUsers if noteItem doesn't have it
            const daiUser = daiBelongUsers?.find(u => u.daicd === daiCd);
            const daiUserGender = daiUser?.sex === 1 ? '男' : '女';

            // If no note record found, create a minimal stub with user data to prevent downstream errors
            if (!noteItem && daiUser) {
                noteItem = {
                    user: {
                        daicd: daiCd,
                        isleave: daiUser.isleave ?? 0,
                        blockcd: blockCd ?? daiUser.blockcd ?? '',
                        unitcd: unitCd ?? daiUser.unitcd ?? '',
                        isWarning: false,
                        isHighlightRecord: true,
                        face_img: daiUser.face_img ?? '',
                        family: daiUser.family ?? '',
                        given: daiUser.given ?? '',
                        kanafamily: daiUser.kanafamily ?? '',
                        kanagiven: daiUser.kanagiven ?? '',
                        sex: daiUser.sex ?? 0,
                        roomcd: daiUser.roomcd ?? '',
                        roomnm: daiUser.roomnm ?? '',
                        unitobjcd: daiUser.unitobjcd ?? '',
                        bedcd: daiUser.bedcd ?? '',
                        romanjiFamily: daiUser.kanafamily ? katakanaToHepburn(daiUser.kanafamily) : '',
                        romanjiGiven: daiUser.kanagiven ? katakanaToHepburn(daiUser.kanagiven) : '',
                    },
                    vital: []
                };
            }

            // Build merged record
            const priorityRecheck: PriorityRecheck = {
                priority: BatheHelper.getPriorityFromBatheType(daiItem.PRIORITYCD as BatheType),
                recheck: this.resolveRecheckTag(noteItem?.vital)
            };

            result.push({
                sortNo: null,
                bathRoomCd: roomId,
                blockCd: blockCd,
                unitCd: unitCd,
                bathWeeks: daiItem.BATHWEEKS,
                bathDays: daiItem.BATHDAYS,
                bathMethod: BatheMethodList.find(m => m.id === daiItem.BATHMETHODCD)?.text || '',
                priority: priorityRecheck,
                priorityCd: daiItem.PRIORITYCD,
                gender: daiUserGender,
                treatmentNoteMsg: { message: daiItem.TREATMENTNOTES || "" },
                ...(noteItem || {})
            });
        }

        // Sort by priority and gender (same as BatheManagementComponent.sortBatheDailyLogs when no BathDaily)
        const sortedResult = BatheHelper.sortByPriority(result, bathRoomRule);

        // Assign sequential sortNo after sorting
        const generatedRecords = sortedResult.map((item, index) => ({
            ...item,
            sortNo: index + 1
        }));

        // Create BathDaily record structure (similar to BatheManagementComponent.performSaveForDate)
        const dateStr = today.replace(/-/g, ''); // Convert YYYY-MM-DD to YYYYMMDD
        const bathDaily = new BathDaily();
        bathDaily.PartitionKey = `NEW_${roomId}`; // Temporary PK, will be replaced by backend
        bathDaily.SortKey = `date_${dateStr}`; // e.g., "date_20260203"
        bathDaily.LogTable = ""; // Empty for new records

        // Build DAIS array from generated records (without note data)
        bathDaily.DAIS = generatedRecords.map((item, index) => {
            const dais = new DAIS();
            dais.DAICD = item?.user?.daicd || '';
            dais.BATHMETHODCD = item.bathMethod ? BatheMethodList.find(m => m.text === item.bathMethod)?.id || 0 : 0;
            dais.SORTNO = item.sortNo ?? (index + 1);
            return dais;
        });

        return { generatedRecords, bathDailyRecord: bathDaily };
    }

    /**
     * Parse raw data from API response
     */
    parseData(curr: any): any[] {
        if (!curr?.jsonData) return [];

        const sortKeyInfo = curr.sortKey?.split("#") ?? [];
        const blockCd = sortKeyInfo[0]?.split("_")[1] ?? null;
        const unitCd = sortKeyInfo[2]?.split("_")[1] ?? null;

        const parsed: any[] = JSON.parse(curr.jsonData) ?? [];

        return parsed.map((item: any) => {
            if (item?.user) {
                item.user.blockcd = blockCd;
                item.user.unitcd = unitCd;
            }

            // Sort and format notice_list
            const notice_list = (item?.notice_list ?? [])
                .sort((a: any, b: any) => new Date(b?.upddate).getTime() - new Date(a?.upddate).getTime())
                .map((notice: any) => ({
                    ...notice,
                    message: `【${notice?.type_name ?? ""} 】： ${notice?.message ?? ""}`,
                }));

            // Sort and enrich attention_list
            const attention_list = (item?.attention_list ?? [])
                .sort((a: any, b: any) => new Date(b?.upddate).getTime() - new Date(a?.upddate).getTime())
                .map((x: any) => ({
                    ...x,
                    isMapByType: true,
                }));

            return {
                ...item,
                notice_list,
                attention_list,
            };
        });
    }

    /**
     * Enrich user data with additional information
     */
    async enrichUserData(recordMerged: any[], officeType: number, isFlashingMoshiokuri: boolean = true, isBatheManagement: boolean = false): Promise<any[]> {
        let lstDaiInfo = this.sessionStorageService.getSessionData(SESSION_STORAGE_KEY.daiInfo) || [];

        const listDaicdInNote = recordMerged?.map((item: any) => item?.user?.daicd) ?? [];
        const listDaicdInDaiInfo = lstDaiInfo?.map((item: any) => item?.daicd) ?? [];

        // Check missing dai => fetching if needed
        const onlyInNoteDaicd = listDaicdInNote.filter(x => !listDaicdInDaiInfo.includes(x));
        if (onlyInNoteDaicd.length > 0) {
            // Optimized: Only fetch daiInfo for new users instead of fetching all
            const officeCd = this.sessionContextService.getOfficeCd();
            const newDaiInfoRes: any = await lastValueFrom(
                this.officeService.getDaiInfoForNewUsers(onlyInNoteDaicd, officeCd, officeType)
            );

            if (newDaiInfoRes?.status === 200 && newDaiInfoRes?.result?.length > 0) {
                lstDaiInfo = newDaiInfoRes.result;
            }
        }

        //Build raw data
        let userData: any[] = CommonHelper.processUserData(
            recordMerged,
            officeType,
            lstDaiInfo,
            isFlashingMoshiokuri,
            false,
            !isBatheManagement ? BATH_SCREEN.SCREEN_ALIAS : ""
        );

        // enrich for each user
        userData = userData.map((data: any) => {
            const user = data?.user ?? {};

            // Office type is 1 or 3 → push notice vào attention list
            if (Utils.isNyusho(officeType) && data?.notice_list?.message)
                data.attention_list = [...(data.attention_list ?? []), data.notice_list];

            // Convert kana → romanji
            if (user.kanafamily)
                user.romanjiFamily = katakanaToHepburn(user.kanafamily);
            if (user.kanagiven)
                user.romanjiGiven = katakanaToHepburn(user.kanagiven);

            return {
                ...data,
                attention_list: [...CommonHelper.distinctObjects(data?.attention_list ?? [])],
                user,
            };
        });

        return userData;
    }


    /**
     * Group and sort data based on configuration
     */
    groupAndSortData(originNotes: any[], options: FetchScreenRecordOptions, bathDailyRoomSet?: Set<string>): any[] {
        const isNyusho = Utils.isNyusho(options.officeType);
        const isFlashingMoshiokuri = options.bathingScreenConfig?.isFlashingMoshiokuri ?? true;

        // Block and Unit filtering
        let filtered = this.filterByBlockUnit(originNotes, options.bathingScreenConfig?.unitSelectedKey);
        filtered = this.filterByGroup(filtered, options.bathingScreenConfig?.groupSelectedKey);
        filtered = this.filterByBathRoom(filtered, options.bathingScreenConfig?.bathRoomSelectedKey || "");

        let uniqueRecords = this.removeDuplicateByDaicd(filtered);

        if (isNyusho)
            uniqueRecords = this.filterDataByDayCareEndTime(uniqueRecords);

        let tempNotes = uniqueRecords;
        switch (options.bathingScreenConfig?.sortType) {
            case BathingScreenSortType.BY_ROOM:
                // isSortByRoom = true;
                tempNotes = CommonHelper.sortDataByKanaName(uniqueRecords, true, options.tokenizer, false, true, options.bathingScreenConfig?.isFlashingMoshiokuri ?? true, true) || [];
                break;
            case BathingScreenSortType.BY_NAME:
                // isSortByRoom = false;
                tempNotes = CommonHelper.sortDataByKanaName(uniqueRecords, false, options.tokenizer, false, true, options.bathingScreenConfig?.isFlashingMoshiokuri ?? true, true) || [];
                break;
            case BathingScreenSortType.BY_BATH_ORDER:
                tempNotes = CommonHelper.sortDataBySortNo(
                    uniqueRecords,
                    true,
                    isFlashingMoshiokuri,
                    true,
                    isNyusho,
                    options.tokenizer,
                    bathDailyRoomSet
                ) || [];
                break;
            default:
                break;
        }

        return tempNotes;
    }

    /**
     * Filter by block and unit
     */
    filterByBlockUnit(currentDatas: any[], valueSelected?: string | string[]): any[] {
        let result = cloneDeep(currentDatas);

        if (Utils.isNullOrEmpty(valueSelected) || (Array.isArray(valueSelected) && valueSelected.length === 0)) {
            return result;
        }

        // Nếu valueSelected là string thì chuyển thành array
        const selectedArray = Array.isArray(valueSelected) ? valueSelected : [valueSelected];

        let filtered: any[] = [];

        for (const selected of selectedArray) {
            if (!selected) continue;

            if (selected.includes("-")) {
                // dạng "block-unit"
                const [blockcd, unitcd] = selected.split("-");
                const matches = result.filter(
                    (x: any) => x.user?.blockcd === blockcd && x.user?.unitcd === unitcd
                );
                filtered = [...filtered, ...matches];
            } else {
                // chỉ block
                const blockcd = selected;
                const matches = result.filter(
                    (x: any) => x.user?.blockcd === blockcd
                );
                filtered = [...filtered, ...matches];
            }
        }

        // Distinct theo daicd
        result = filtered.filter((v, i, a) => a.findIndex(t => t.user?.daicd === v.user?.daicd) === i);

        return result;
    }

    /**
     * Filter by group
     */
    filterByGroup(currentDatas: any[], valueSelected?: string | string[]): any {
        let result = cloneDeep(currentDatas);

        if (Utils.isNullOrEmpty(valueSelected) || (Array.isArray(valueSelected) && valueSelected.length === 0)) {
            return result;
        }

        const sessionGr = this.sessionStorageService.getSessionData(LOCAL_STORAGE_KEY.groupData);

        // Nếu valueSelected là string thì chuyển thành array
        const selectedArray = Array.isArray(valueSelected) ? valueSelected : [valueSelected];

        // Lấy tất cả daicd cần filter
        let daiLstForFilter: any[] = [];

        // Duyệt từng group được chọn
        for (const selected of selectedArray) {
            if (!selected) continue;

            const groupData = sessionGr?.find((gr: any) => gr.value === selected);
            const daiLst = groupData?.daiList;

            if (daiLst?.length >= 0) {
                // Trường hợp group nhỏ (có danh sách daiList)
                daiLstForFilter = [...daiLstForFilter, ...daiLst];
            } else {
                // Trường hợp group lớn (groupKbn)
                const [blockCd, unitCd, groupKbnCd] = selected.split("-");

                const grDataByGrKbn = sessionGr?.filter(
                    (gr: any) =>
                        gr?.value?.split("-")[0] === blockCd &&
                        gr?.value?.split("-")[1] === unitCd &&
                        gr?.value?.split("-")[2] === groupKbnCd &&
                        gr?.daiList?.length >= 0
                );

                if (grDataByGrKbn?.length > 0) {
                    grDataByGrKbn.forEach((gr: any) => {
                        daiLstForFilter = [...daiLstForFilter, ...gr.daiList];
                    });
                }
            }
        }

        // Distinct danh sách daiCd
        daiLstForFilter = daiLstForFilter?.filter(
            (value, index, self) => self?.indexOf(value) === index
        );

        // Filter dữ liệu theo tất cả daicd
        result = result?.filter((csh: any) => daiLstForFilter?.includes(csh?.user?.daicd));

        return result;
    }

    /**
     * Filter by bathRoom
     */
    /**
     * Filter by bathRoom
     * @param currentDatas - User records to filter
     * @param valueSelected - Selected bathroom key in format "blockCd|unitCd|bathroomUUID"
     * @returns Filtered user records
     */
    filterByBathRoom(currentDatas: any, valueSelected: string) {
        let result = cloneDeep(currentDatas);
        const sessionBathRoom = this.bathRoomInfos;
        
        if (Utils.isNullOrEmpty(valueSelected)) {
            return result;
        }

        // Extract bathroom UUID from valueSelected (format: "blockCd|unitCd|bathroomUUID")
        // Example: "02|001|9f93eb1d-f9eb-47c4-a82e-6a3554e1a401" → "9f93eb1d-f9eb-47c4-a82e-6a3554e1a401"
        const parts = valueSelected.split('|');
        const bathroomUUID = parts[parts.length - 1]; // Get last item

        // Find bath master where SortKey includes the bathroom UUID
        const bathRoomData = sessionBathRoom?.find((br: any) => 
            br.SortKey?.includes(bathroomUUID)
        );

        if (!bathRoomData) {
            // Bathroom not found, return empty result
            return [];
        }

        // Filter users by bathRoomCd
        result = result?.filter((csh: any) => csh?.bathRoomCd === bathroomUUID);

        return result;
    }

    // CKWEB-1972 Sort theo BLOCKCD và UNITCD, sau đó mới đến SORTNO, nếu dữ liệu không có SORTNO thì sort theo INSDATE.
    sortBathRoomBySortNo(bathMastersRaw: BathMaster[]): BathMaster[] {
        // Normalize sortNo for items without it based on INSDATE
        const bathMasters = this.normalizeSortNoByInsDate([...bathMastersRaw]);
        
        const sortedBathMasters = bathMasters.sort((a, b) => {
            // BLOCKCD = '00' lên đầu.
            if ((a.BLOCKCD === '00') !== (b.BLOCKCD === '00')) {
                return a.BLOCKCD === '00' ? -1 : 1;
            }

            const blockCompare = (a.BLOCKCD || '').localeCompare(b.BLOCKCD || '');
            if (blockCompare !== 0) return blockCompare;

            // UNITCD = '000' lên đầu.
            if ((a.UNITCD === '000') !== (b.UNITCD === '000')) {
                return a.UNITCD === '000' ? -1 : 1;
            }

            const unitCompare = (a.UNITCD || '').localeCompare(b.UNITCD || '');
            if (unitCompare !== 0) return unitCompare;

            const hasSortA = a.SORTNO !== null && a.SORTNO !== undefined && a.SORTNO !== 0;
            const hasSortB = b.SORTNO !== null && b.SORTNO !== undefined && b.SORTNO !== 0;

            // Nếu cả 2 có SORTNO -> sort theo SORTNO
            if (hasSortA && hasSortB) {
                return (a.SORTNO as number) - (b.SORTNO as number);
            }

            // Nếu chỉ 1 thằng có SORTNO -> ưu tiên thằng có SORTNO
            if (hasSortA !== hasSortB) {
                return hasSortA ? -1 : 1;
            }

            // Không có SORTNO -> sort theo INSDATE
            const dateA = a.INSDATE ? new Date(a.INSDATE).getTime() : 0;
            const dateB = b.INSDATE ? new Date(b.INSDATE).getTime() : 0;

            console.log({dateA, dateB, insA: a.INSDATE, insB: b.INSDATE});

            return dateA - dateB; // cũ -> mới
        });

        return sortedBathMasters;
    }

    /**
     * Chuẩn hóa sortNo dựa trên insDate
     * Nếu sortNo = 0 hoặc không tồn tại, sẽ được đánh số dựa trên insDate
     * Thằng nào có insDate cũ nhất (sớm nhất) được sortNo = 1, mới nhất được sortNo lớn nhất
     */
    private normalizeSortNoByInsDate(bathMasters: BathMaster[]): BathMaster[] {
        if (!bathMasters || bathMasters.length === 0) return bathMasters;

        // Group bathrooms by level (BLOCKCD, UNITCD)
        const groupMap = new Map<string, BathMaster[]>();

        bathMasters.forEach(bath => {
            const key = `${bath.BLOCKCD || ''}_${bath.UNITCD || ''}`;

            if (!groupMap.has(key)) {
                groupMap.set(key, []);
            }
            groupMap.get(key)?.push(bath);
        });

        // Process each group
        groupMap.forEach((baths) => {
            // Find baths without sortNo (sortNo = 0 or not set)
            const needsSort = baths.filter(b => !b.SORTNO || b.SORTNO === 0);

            if (needsSort.length > 0) {
                // Sort by insDate ascending (cũ nhất = sortNo nhỏ nhất, mới nhất = sortNo lớn nhất)
                const sorted = needsSort.sort((a, b) => {
                    const aHasSort = a.SORTNO !== null && a.SORTNO !== undefined;
                    const bHasSort = b.SORTNO !== null && b.SORTNO !== undefined;

                    // Ưu tiên thằng có SORTNO
                    if (aHasSort && bHasSort) {
                        const sortA = a.SORTNO as number;
                        const sortB = b.SORTNO as number;
                        return sortA - sortB;
                    }

                    if (aHasSort) return -1;
                    if (bHasSort) return 1;

                    // Cả 2 chưa có SORTNO -> sort theo INSDATE
                    const dateA = new Date(a.INSDATE || '').getTime();
                    const dateB = new Date(b.INSDATE || '').getTime();
                    return dateA - dateB;
                });

                // Assign sortNo
                sorted.forEach((bath, index) => {
                    bath.SORTNO = index + 1;
                });
            }
        });

        return bathMasters;
    }

    async getBathroomInfos(): Promise<void> {
        try {
            this.loadingScreenCoordinator.show();
            const res: any = await lastValueFrom(this.bathRoomSettingService.getBathroomSummary());

            if (res?.status === 200 && Array.isArray(res?.result)) {
                // Fetch and map bath masters data (similar to BatheManagementComponent.getBathroomSummaries)
                const bathMastersRaw: BathMaster[] = res.result;

                // Sort by BLOCKCD and UNITCD and then by SORTNO.
                const sortedBathMasters = this.sortBathRoomBySortNo(bathMastersRaw);

                // Store bath masters with parsed BATHDAIS
                this.bathRoomInfos = sortedBathMasters.map((master: BathMaster) => {
                    return {
                        ...master, 
                        // Parse DaiTable to get BATHDAIS using helper
                        BATHDAIS: parseDaiTableToBathDais(master.DaiTable || ''),
                        DaiTable: ""
                    };
                });
            }
        }
        catch (error) {
            console.error('Error fetching bath room summary data:', error);
        }
        finally {
            this.loadingScreenCoordinator.hide();
        }
    }

    /**
     * Generate bathroom dropdown options from stored bath masters data
     * This provides backward compatibility for components expecting dropdown format
     * @returns Array of dropdown options with empty option prepended
     */
    genBathRoomSelectOptions(): any[] {
        const bathMasters = this.bathRoomInfos || [];
        
        if (bathMasters.length === 0) {
            return [];
        }

        const dropdownBathRooms: any[] = [];

        for (const master of bathMasters) {
            const bathRoomCd = DynamoDBKeyHelper.getValueByClass(master.SortKey, "bathroom") ?? "";
            const bathDaisArray = master.BATHDAIS || [];
            const daiList = bathDaisArray
                .map((d: any) => d?.DAICD)
                .filter((x: any): x is string => !!x);

            dropdownBathRooms.push({
                name: master.BATHNAME || "",
                value:
                    (master.BLOCKCD || "00") +
                    "|" +
                    (master.UNITCD || "000") +
                    "|" +
                    bathRoomCd,
                daiList: daiList
            });
        }

        return dropdownBathRooms.length > 0
            ? [{ name: "　", value: "" }, ...dropdownBathRooms]
            : [];
    }

    /**
     * Remove duplicates by daicd
     */
    removeDuplicateByDaicd(data: any[]): any[] {
        return data.filter(
            (obj, index, arr) =>
                index === arr.findIndex((o: any) => o.user?.daicd === obj.user?.daicd)
        );
    }

    /**
     * Filter bathing data by day care dates (for Nyusho only).
     */
    filterDataByDayCareEndTime = (result: any[]) => {
        const now = moment(new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Tokyo' })));
        return result.map((item: any) => {
            const { start_time, end_time } = item?.day_care || {};
            if (!start_time || !end_time) return item;

            const isWithinRange = moment(start_time).isSameOrBefore(now, 'day')
                && moment(end_time).isSameOrAfter(now, 'day');
            if (isWithinRange) return item;

            // Outside day care time range: keep record but clear note fields
            return { ...item, bathe_method: null, bathe: null, attention_list: null, vital: null };
        });
    };

    /**
     * Bathe-specific filtering logic
     * Returns filtered records along with lists of daiCds for CARRY_OUT and CANCEL statuses
     */
    batheFiltering(userRecords: any[]): {
        temptNodes: any[],
        daiCdsCarryOut: string[],
        daiCdsCancel: string[]
    } {
        const daiCdsCarryOut: string[] = [];
        const daiCdsCancel: string[] = [];

        let temptNodes = userRecords.map(item => {
            const batheRecords = Array.isArray(item?.bathe) ? item.bathe : item?.bathe ? [item.bathe] : [];
            // Get today's records
            const todayBatheRecords = CommonHelper.filterByTodayJP(batheRecords ?? []);
            const isTodayRecord = Array.isArray(todayBatheRecords) && todayBatheRecords.length > 0;

            if (isTodayRecord && todayBatheRecords.some(e => e?.status_kbn === KbnStatus.STATUS_CARRY_OUT)) {
                // Collect daiCd for CARRY_OUT status
                const daiCd = item?.user?.daicd;
                if (daiCd) {
                    daiCdsCarryOut.push(daiCd);
                }
                // CKWEB-924 phương pháp tắm là Lau mình (清拭)
                const batheMethodRecords = Array.isArray(item?.bathe_method) ? item.bathe_method : item?.bathe_method ? [item.bathe_method] : [];
                const todayBathMethodRecords = CommonHelper.filterByTodayJP(batheMethodRecords ?? []);
                const bathWipe = todayBathMethodRecords.some((e:any) => e?.content === LeaveType.Wipe);
                if (bathWipe){
                    return {
                        ...item,
                        index: {
                            ...(item.index ?? {}),
                            value: LeaveType.Wipe,
                            originalIndex: item?.index?.value
                        }
                    };
                }
                return null; // Today + carry out => filter out
            }

            const batheCancel = todayBatheRecords.some((e: any) => e?.status_kbn === KbnStatus.STATUS_CANCEL);

            // Collect daiCd for CANCEL status
            if (batheCancel) {
                const daiCd = item?.user?.daicd;
                if (daiCd) {
                    daiCdsCancel.push(daiCd);
                }
            }
            else { //Handle WAIT status, last priority
                let priorityRecheck: any = {};

                try { // prevent JSON parse error
                    if (item?.priority) {
                        priorityRecheck = typeof item.priority === 'string'
                            ? JSON.parse(item.priority)
                            : item.priority;
                    }
                } catch (err) {
                    priorityRecheck = {};
                }
                if (!item.vital?.length || priorityRecheck?.recheck?.includes("再検")) {
                        return {
                        ...item,
                        index: {
                            ...(item.index ?? {}),
                            value: LeaveType.Wait,
                            originalIndex: item?.index?.value
                        }
                    };
                }
            }
            
            return {
                ...item,
                index: {
                    ...(item.index ?? {}),
                    value: batheCancel ? LeaveType.Cancel : item?.index?.value,
                    originalIndex: item?.index?.value
                }
            };
        }).filter(item => item !== null);

        // Re-map index value after sort
        temptNodes = temptNodes
            .sort(this.sortByOriginalIndex)
            .map((e, idx) => {
                const rawVal = e.index?.value;
                const numVal = Number(rawVal);

                return {
                    ...e,
                    index: {
                        ...e.index,
                        value: isNaN(numVal) || String(rawVal).trim() === ""
                            ? rawVal
                            : idx + 1
                    }
                };
            });

        // CKWEB-1827 đối ứng cho record CarryOut và Cancel đẩy xuống cuối cùng của phòng.
        temptNodes = this.sortCancelCarryOut(temptNodes, daiCdsCarryOut);

        return {
            temptNodes,
            daiCdsCarryOut,
            daiCdsCancel
        };
    }

    sortCancelCarryOut(records: any[], carryOutUserIds: string[]) {
        // Group records theo bathRoomCd (key: bathRoomCd, value: list records)
        const recordsByBathRoom = new Map<string, any[]>();

        records.forEach(record => {
            const bathRoomCd = record?.bathRoomCd ?? '';
            if (!recordsByBathRoom.has(bathRoomCd)) {
                recordsByBathRoom.set(bathRoomCd, []);
            }
            const bathRoomRecords = recordsByBathRoom.get(bathRoomCd);
            if (bathRoomRecords) {
                bathRoomRecords.push(record);
            }
        });

        let sortedRecords: any[] = [];

        recordsByBathRoom.forEach(bathRoomRecords => {
            // List bình thường (không Cancel và không CarryOut)
            const normalRecords = bathRoomRecords.filter(x => 
                x?.index?.value !== LeaveType.Cancel && !carryOutUserIds.includes(x?.user?.daicd)
            );

            // List đưa xuống cuối (Cancel hoặc CarryOut)
            const bottomRecords = bathRoomRecords.filter(x => 
                x?.index?.value === LeaveType.Cancel || carryOutUserIds.includes(x?.user?.daicd)
            );

            // Đưa Cancel và CarryOut xuống cuối theo từng phòng
            sortedRecords.push(...normalRecords, ...bottomRecords);
        });

        return sortedRecords;
    }

    /**
     * If value is wait, hold its original index for sorting, but if it is another (waipe, cancel), make it last in order
     */
    sortByOriginalIndex(a: any, b: any): number {
        const valA = a?.index?.value;
        const valB = b?.index?.value;

        const origIndexA = a?.index?.originalIndex ?? 0;
        const origIndexB = b?.index?.originalIndex ?? 0;

        const isTextA = typeof valA === "string" && valA !== LeaveType.Wait;
        const isTextB = typeof valB === "string" && valB !== LeaveType.Wait;

        if (isTextA && isTextB) {
            // CKWEB-924 sort 清拭 trước 中止
            const getPriority = (val: string | number) => {
                if (val === LeaveType.Wipe) return 1;
                if (val === LeaveType.Cancel) return 2;
                return 99; 
            };

            const prioA = getPriority(valA);
            const prioB = getPriority(valB);

            return prioA !== prioB ? prioA - prioB : origIndexA - origIndexB; //priority wipe before cancel
        }

        if (isTextA && !isTextB) return 1;  // a should come after b ==> a is Cancel, Wipe

        if (!isTextA && isTextB) return -1; // b should combere after a ==> b is Cancel, Wipe

        return origIndexA - origIndexB;
    }

    /**
     * Custom sort for index values
     */
    customIndexSort(a: any, b: any): number {
        // CKWEB-924 sort 清拭 trước 中止
        if (a?.index?.value === LeaveType.Wipe && b?.index?.value === LeaveType.Cancel) return -1;
        if (a?.index?.value === LeaveType.Cancel && b?.index?.value === LeaveType.Wipe) return 1;

        const isNumA = typeof a?.index?.value === "number" || (!isNaN(Number(a?.index?.value)) && a?.index?.value.trim() !== "");
        const isNumB = typeof b?.index?.value === "number" || (!isNaN(Number(b?.index?.value)) && b?.index?.value.trim() !== "");
        if (isNumA && !isNumB) return -1;
        if (!isNumA && isNumB) return 1;
        if (isNumA && isNumB) return Number(a?.index?.value) - Number(b?.index?.value);
        return String(a?.index?.value).localeCompare(String(b?.index?.value));
    }

    /**
     * Filter only necessary properties
     */
    necessaryPropertyFiltering<T extends Record<string, any>>(data: T[], necessaryProperties: string[]): Record<string, any>[] {
        return data.map(item =>
            necessaryProperties.reduce<Record<string, any>>((filtered, prop) => {
                if (prop === "bathe_method_content") {
                    filtered[prop] = item["bathe_method"]?.content ?? "";
                } else {
                    filtered[prop] = item[prop] ?? null;
                }
                return filtered;
            }, {})
        );
    }

    /**
     * Get empty result structure
     */
    private getEmptyResult(): FetchScreenRecordResult {
        return {
            originNotes: [],
            processedNotes: [],
            bathingNoteRecords: [],
            daiCdsCarryOut: [],
            daiCdsCancel: [],
            pendingBathDailyRecords: []
        };
    }
    
    private parseBathDailyResponse(apiResult: any): BathDailyModel[] {
        const bathDailyData: BathDailyModel[] = [];

        if (apiResult && this.successfulHttpStatusCodes.includes(apiResult?.status ?? 500)) {
            try {
                if (apiResult?.result?.isSuccessStatusCode && apiResult?.result?.json) {
                    const dailyData = JSON.parse(apiResult.result.json) ?? [];

                    bathDailyData.push(...dailyData.map((item: any) => {
                        const bMethod = BatheMethodList.find(mt => mt?.id === item?.bathMethodCd);
                        const model = new BathDailyModel();
                        Object.assign(model, {
                            corpcd: item.corpcd ?? '',
                            officecd: item.officecd ?? '',
                            blockcd: item.blockcd ?? '',
                            subcd: item.subcd ?? '',
                            unitcd: item.unitcd ?? '',
                            daicd: item.daicd ?? '',
                            bathRoomCd: item.bathRoomCd ?? 0,
                            bathDate: item.bathDate ? new Date(item.bathDate) : new Date(),
                            bathMethodCd: item.bathMethodCd ?? null,
                            bathMethod: bMethod?.text ?? "",
                            sortNo: item.sortNo ?? null,
                            insStfcd: item.insStfcd ?? null,
                            insDate: item.insDate ? new Date(item.insDate) : null,
                            updStfcd: item.updStfcd ?? null,
                            updDate: item.updDate ? new Date(item.updDate) : null
                        });
                        return model;
                    }));
                }
            } catch (error) {
                console.error(`Error mapping bath daily data:`, error);
            }
        }

        return bathDailyData;
    }

    /**
     * Convert BATHRULE number to BathRoomRule enum
     * Same mapping as BatheManagementComponent
     * @param bathRule - BATHRULE value from BathMaster (0, 1, 2)
     * @returns BathRoomRule enum value
     */
    private getBathRoomRuleFromBathRule(bathRule: number | undefined): BathRoomRule {
        switch (bathRule) {
            case 0:
                return BathRoomRule.None;
            case 1:
                return BathRoomRule.MenFirst;
            case 2:
                return BathRoomRule.WomenFirst;
            default:
                return BathRoomRule.None;
        }
    }

    /**
     * Perform asynchronous save for pending bath daily records
     * Saves room by room in parallel without blocking UI
     * This runs in the service context and persists across component lifecycle
     * @param pendingRecords - Array of BathDaily records to save
     * @param dailyRecordLake - Reference to cache array to update
     */
    async performAsyncBathDailySave(pendingRecords: BathDaily[], dailyRecordLake: BathDaily[]): Promise<void> {
        if (!pendingRecords || pendingRecords.length === 0) return;

        // Set async save flag and counters
        this.isAsyncSavingBathDaily = true;
        this.asyncSaveCompletedCount = 0;
        this.asyncSaveTotalCount = pendingRecords.length;

        console.log(`[Async Save Service] Starting save for ${this.asyncSaveTotalCount} bathrooms...`);

        // Create parallel save promises for all pending records
        const savePromises = pendingRecords.map(bathDaily =>
            this.saveSingleBathDaily(bathDaily, dailyRecordLake)
        );

        try {
            // Execute all saves in parallel and wait for completion
            await Promise.all(savePromises);
            console.log(`[Async Save Service] All ${this.asyncSaveTotalCount} bathrooms saved successfully!`);
            this.onAsyncSaveComplete(true);
        } catch (error) {
            console.error('[Async Save Service] Error during async save:', error);
            this.onAsyncSaveComplete(false);
        }
    }

    /**
     * Save a single bath daily record and update cache
     * Core logic from BatheManagementComponent.performSaveForDate
     * @param bathDaily - BathDaily record to save
     * @param dailyRecordLake - Reference to cache array to update
     */
    private async saveSingleBathDaily(bathDaily: BathDaily, dailyRecordLake: BathDaily[]): Promise<void> {
        try {
            const isNew = bathDaily.PartitionKey?.startsWith('NEW_');

            const upsertRes: any = await lastValueFrom(
                // suppressSignal: this is the schedule's own derived ISVALID write-back, not a
                // human edit — signalling it would refresh every device and loop back here.
                this.bathRoomSettingService.updateLogs([bathDaily], true)
            );

            if (upsertRes?.status === 200 && (upsertRes?.result || []).length > 0) {
                const saved = upsertRes.result[0];
                // Absent on servers that predate this field - treat as written.
                const outcome: string = saved?.outcome ?? 'written';
                if (outcome !== 'written') {
                    // The day moved on under this derived write-back (a dai moved in, a save from
                    // 入浴管理), or is gone. Nothing was stored. There is no user to ask, so pull the
                    // stored day into the cache: the next render recomputes against it and, if
                    // ISVALID still differs, queues this write-back again at the right version.
                    console.warn(`[Async Save Service] Write-back refused (${outcome}) for ${bathDaily.PartitionKey}/${bathDaily.SortKey}; stored ITEMVER ${saved?.currentItemVer}`);
                    await this.reloadDayIntoCache(bathDaily, dailyRecordLake);
                    return;
                }

                if (isNew) {
                    // INSERT: add new record to cache
                    const newBathDaily = new BathDaily();
                    newBathDaily.PartitionKey = saved.partitionKey;
                    newBathDaily.SortKey = saved.sortKey;
                    newBathDaily.LogTable = saved.logTable || '';
                    newBathDaily.DAIS = bathDaily.DAIS;
                    newBathDaily.INSSTFCD = saved.insstfcd;
                    newBathDaily.INSDATE = saved.insdate;
                    newBathDaily.ITEMVER = saved.itemver ?? null;
                    dailyRecordLake.push(newBathDaily);
                } else {
                    // UPDATE: update existing record in cache
                    const existingIdx = dailyRecordLake.findIndex(
                        r => r.PartitionKey === bathDaily.PartitionKey && r.SortKey === bathDaily.SortKey
                    );
                    if (existingIdx !== -1) {
                        dailyRecordLake[existingIdx].DAIS = bathDaily.DAIS;
                        // The version the day is now stored at; the next write-back must send it.
                        dailyRecordLake[existingIdx].ITEMVER = saved.itemver ?? dailyRecordLake[existingIdx].ITEMVER;
                        // Every written upsert clears the day's force-valid flag server-side.
                        dailyRecordLake[existingIdx].FORCEVALIDDAIS = null;
                    } else {
                        // Fallback: push if not found in cache
                        const updated = new BathDaily();
                        updated.PartitionKey = saved.partitionKey;
                        updated.SortKey = saved.sortKey;
                        updated.DAIS = bathDaily.DAIS;
                        updated.ITEMVER = saved.itemver ?? null;
                        dailyRecordLake.push(updated);
                    }
                }

                this.asyncSaveCompletedCount++;
                console.log(`[Async Save Service] Saved bathroom ${this.asyncSaveCompletedCount}/${this.asyncSaveTotalCount}`);
            } else {
                console.warn('[Async Save Service] Failed to save bath daily:', upsertRes);
            }
        } catch (error) {
            console.error('[Async Save Service] Error saving single bath daily:', error);
            throw error;
        }
    }

    /**
     * Replaces one day in the cache with what the server stores (or drops it when the server
     * has no such day), after a refused write-back. Only the cache is touched; the caller's
     * render cycle owns recomputation.
     */
    private async reloadDayIntoCache(bathDaily: BathDaily, dailyRecordLake: BathDaily[]): Promise<void> {
        const bathroomUID = DynamoDBKeyHelper.getValueByClass(bathDaily.PartitionKey, 'bathroom') ?? '';
        const ymd = (bathDaily.SortKey ?? '').replace('date_', '');
        if (!bathroomUID || ymd.length !== 8) return;
        const dateStr = `${ymd.substring(0, 4)}-${ymd.substring(4, 6)}-${ymd.substring(6, 8)}`;
        const officeCd = this.sessionContextService.getOfficeSelected<any>()?.officeCd ?? '';

        try {
            const response: any = await lastValueFrom(
                this.bathRoomSettingService.getBathLogs({ officeCd, bathroomUID, from: dateStr, to: dateStr })
            );
            const item = this.successfulHttpStatusCodes.includes(response?.status ?? 500)
                ? (response?.result ?? []).find((r: any) => r.sortKey === bathDaily.SortKey)
                : undefined;

            const idx = dailyRecordLake.findIndex(
                r => r.PartitionKey === bathDaily.PartitionKey && r.SortKey === bathDaily.SortKey
            );
            if (!item) {
                if (idx !== -1) dailyRecordLake.splice(idx, 1);
                return;
            }

            const fresh = new BathDaily();
            fresh.PartitionKey = item.partitionKey || '';
            fresh.SortKey = item.sortKey || '';
            fresh.LogTable = item.logTable || '';
            fresh.INSSTFCD = item.insstfcd;
            fresh.INSDATE = item.insdate;
            fresh.UPDSTFCD = item.updstfcd;
            fresh.UPDDATE = item.upddate;
            fresh.SCHEMAVER = item.schemaver ?? null;
            fresh.NEEDSRECALC = item.needsrecalc ?? null;
            fresh.FORCEVALIDDAIS = item.forcevaliddais ?? null;
            fresh.ITEMVER = item.itemver ?? null;
            fresh.DAIS = mapJsonDais(item.dais);
            if (idx !== -1) dailyRecordLake[idx] = fresh;
            else dailyRecordLake.push(fresh);
        } catch (error) {
            console.error('[Async Save Service] Failed to reload a refused day:', error);
        }
    }

    /**
     * Handle async save completion
     * @param success - Whether all saves completed successfully
     */
    private onAsyncSaveComplete(success: boolean): void {
        this.isAsyncSavingBathDaily = false;

        if (success) {
            console.log(`[Async Save Service] ✓ Successfully saved ${this.asyncSaveCompletedCount} bath daily records`);
        } else {
            console.error(`[Async Save Service] ✗ Failed to save some bath daily records`);
        }

        // Reset counters
        this.asyncSaveCompletedCount = 0;
        this.asyncSaveTotalCount = 0;
    }
}
